import { logEvent } from 'firebase/analytics';
import { collection, doc, documentId, getDoc, getDocs, orderBy, query, where } from 'firebase/firestore';
import { analyticsInstance, db } from '@/firebase';
import { mpTrack } from '@/services/mixpanel';
import type { AnalyticsSummary, DailyAnalytics } from '@/types';

/** Fan an event out to every configured analytics sink (Firebase + Mixpanel). */
export function trackEvent(name: string, params?: Record<string, string | number | boolean>): void {
  const analytics = analyticsInstance();
  if (analytics) logEvent(analytics, name, params);
  mpTrack(name, params);
}

export function dateKey(date: Date): string {
  return date.toISOString().slice(0, 10);
}

function mapDaily(id: string, data: Record<string, unknown>): DailyAnalytics {
  return {
    date: id,
    dailyUsers: Number(data.dailyUsers ?? 0),
    totalDownloads: Number(data.totalDownloads ?? 0),
    platforms: (data.platforms as DailyAnalytics['platforms']) ?? {},
    formats: (data.formats as DailyAnalytics['formats']) ?? {},
    processingMsTotal: Number(data.processingMsTotal ?? 0),
    processingCount: Number(data.processingCount ?? 0),
  };
}

function emptyDay(date: string): DailyAnalytics {
  return {
    date,
    dailyUsers: 0,
    totalDownloads: 0,
    platforms: {},
    formats: {},
    processingMsTotal: 0,
    processingCount: 0,
  };
}

/**
 * Admin-only: pull the last N daily analytics docs plus the running summary,
 * filling gaps with zero-days so charts stay continuous.
 */
export async function fetchAdminAnalytics(
  rangeDays = 14
): Promise<{ days: DailyAnalytics[]; summary: AnalyticsSummary | null }> {
  const startDate = dateKey(new Date(Date.now() - (rangeDays - 1) * 86_400_000));
  const endDate = dateKey(new Date());

  const q = query(
    collection(db(), 'analytics'),
    where(documentId(), '>=', startDate),
    where(documentId(), '<=', endDate),
    orderBy(documentId(), 'asc')
  );

  const [snapshot, summarySnapshot] = await Promise.all([
    getDocs(q),
    getDoc(doc(db(), 'analytics', 'summary')),
  ]);

  const byDate = new Map(snapshot.docs.map((d) => [d.id, mapDaily(d.id, d.data())]));
  const days: DailyAnalytics[] = [];
  for (let i = rangeDays - 1; i >= 0; i -= 1) {
    const date = dateKey(new Date(Date.now() - i * 86_400_000));
    days.push(byDate.get(date) ?? emptyDay(date));
  }

  let summary: AnalyticsSummary | null = null;
  if (summarySnapshot.exists()) {
    const data = summarySnapshot.data();
    summary = {
      totalDownloads: Number(data.totalDownloads ?? 0),
      totalUsers: Number(data.totalUsers ?? 0),
      platforms: (data.platforms as AnalyticsSummary['platforms']) ?? {},
      formats: (data.formats as AnalyticsSummary['formats']) ?? {},
      processingMsTotal: Number(data.processingMsTotal ?? 0),
      processingCount: Number(data.processingCount ?? 0),
    };
  }

  return { days, summary };
}
